// Tab "Risposte" della vista admin globale (solo super_admin): storico delle
// modifiche alle disponibilita' (session_response_events), raggruppato per
// sessione. Evidenzia chi ha cambiato piu' volte e, per il link pubblico, lo
// stesso browser o IP che ha risposto per piu' giocatori.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { History, Search } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { supabaseV2 } from "../../lib/supabase";
import { EmptyState, SectionLoading } from "../../components/state";

type EventRow = {
  id: string;
  team_id: string;
  team_name: string;
  session_id: string;
  session_title: string | null;
  session_starts_at: string | null;
  player_id: string;
  player_name: string;
  op: "insert" | "update" | "delete";
  old_status: string | null;
  new_status: string | null;
  old_note: string | null;
  new_note: string | null;
  source: string | null;
  actor_user_id: string | null;
  actor_email: string | null;
  token_id: string | null;
  token_label: string | null;
  token_personal: boolean | null;
  client_id: string | null;
  ip: string | null;
  user_agent: string | null;
  changed_at: string;
};

type SessionGroup = {
  session_id: string;
  team_name: string;
  title: string;
  starts_at: string | null;
  events: EventRow[];
  // giocatori toccati dallo stesso browser / ip dal link pubblico
  sharedClients: Map<string, Set<string>>;
  sharedIps: Map<string, Set<string>>;
  changesByPlayer: Map<string, number>;
};

const PERIODS = [1, 7, 30, 90] as const;

function groupBySession(rows: EventRow[]): SessionGroup[] {
  const groups = new Map<string, SessionGroup>();
  for (const e of rows) {
    let g = groups.get(e.session_id);
    if (!g) {
      g = {
        session_id: e.session_id,
        team_name: e.team_name,
        title: e.session_title || "",
        starts_at: e.session_starts_at,
        events: [],
        sharedClients: new Map(),
        sharedIps: new Map(),
        changesByPlayer: new Map(),
      };
      groups.set(e.session_id, g);
    }
    g.events.push(e);
    g.changesByPlayer.set(e.player_id, (g.changesByPlayer.get(e.player_id) || 0) + 1);
    if (e.source === "public_link") {
      if (e.client_id) {
        const set = g.sharedClients.get(e.client_id) || new Set<string>();
        set.add(e.player_id);
        g.sharedClients.set(e.client_id, set);
      }
      if (e.ip) {
        const set = g.sharedIps.get(e.ip) || new Set<string>();
        set.add(e.player_id);
        g.sharedIps.set(e.ip, set);
      }
    }
  }
  for (const g of groups.values()) {
    for (const [k, set] of g.sharedClients) if (set.size < 2) g.sharedClients.delete(k);
    for (const [k, set] of g.sharedIps) if (set.size < 2) g.sharedIps.delete(k);
    g.events.sort((a, b) => a.changed_at.localeCompare(b.changed_at));
  }
  return [...groups.values()].sort((a, b) => (b.starts_at || "").localeCompare(a.starts_at || ""));
}

export function ResponseEventsAdminSection({
  teams,
  intlLocale,
}: {
  teams: Array<{ id: string; name: string }>;
  intlLocale: string;
}) {
  const { t } = useTranslation("settings");
  const [teamId, setTeamId] = useState<string>("");
  const [days, setDays] = useState<(typeof PERIODS)[number]>(7);
  const [search, setSearch] = useState("");

  const eventsQuery = useQuery({
    queryKey: ["admin-response-events", teamId, days],
    queryFn: async () => {
      const from = new Date(Date.now() - days * 24 * 3600_000).toISOString();
      const { data, error } = await supabaseV2.rpc("admin_session_response_events", {
        _team_id: teamId || null,
        _from: from,
        _to: new Date(Date.now() + 60_000).toISOString(),
        _limit: 2000,
      });
      if (error) throw error;
      return (data || []) as EventRow[];
    },
    staleTime: 30_000,
  });

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = (eventsQuery.data || []).filter((e) =>
      !needle ||
      e.player_name.toLowerCase().includes(needle) ||
      (e.session_title || "").toLowerCase().includes(needle) ||
      (e.actor_email || "").toLowerCase().includes(needle) ||
      e.team_name.toLowerCase().includes(needle),
    );
    return groupBySession(rows);
  }, [eventsQuery.data, search]);

  const fmtTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(intlLocale, { dateStyle: "short", timeStyle: "medium" }) : "—";
  const statusLabel = (s: string | null) => (s ? t(`admin.responses.status.${s}`, { defaultValue: s }) : "∅");
  const sourceLabel = (e: EventRow) => {
    if (e.source === "public_link") {
      return e.token_id
        ? e.token_personal
          ? t("admin.responses.viaPersonalLink")
          : t("admin.responses.viaTeamLink", { label: e.token_label || "" })
        : t("admin.responses.viaPublicLink");
    }
    if (e.source === "player_area") return t("admin.responses.viaPlayerArea");
    if (e.source === "staff_override") return t("admin.responses.viaStaff");
    return e.source || "—";
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <History className="h-5 w-5" />
          {t("admin.responses.title")}
        </CardTitle>
        <CardDescription>{t("admin.responses.desc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="h-9 rounded-2xl bg-background px-2 text-sm"
            aria-label={t("admin.responses.team")}
          >
            <option value="">{t("admin.responses.allTeams")}</option>
            {teams.map((tm) => (
              <option key={tm.id} value={tm.id}>{tm.name}</option>
            ))}
          </select>
          <div className="inline-flex rounded-2xl bg-background p-0.5 text-sm">
            {PERIODS.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={cn(
                  "px-2.5 py-1 rounded font-medium transition-colors",
                  days === d ? "bg-card text-foreground " : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t("admin.responses.days", { count: d })}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.responses.searchPlaceholder")}
              className="pl-8 h-9"
            />
          </div>
        </div>

        {eventsQuery.isLoading && <SectionLoading />}
        {eventsQuery.error && (
          <p className="text-sm text-rose-600">{(eventsQuery.error as any)?.message || String(eventsQuery.error)}</p>
        )}
        {!eventsQuery.isLoading && !eventsQuery.error && groups.length === 0 && (
          <EmptyState text={t("admin.responses.empty")} />
        )}

        {groups.map((g) => (
          <div key={g.session_id} className="rounded-2xl border overflow-hidden">
            <div className="px-3 py-2 bg-muted/40 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-semibold">{g.title || t("admin.responses.untitled")}</span>
              <span className="text-muted-foreground">{g.team_name} · {fmtTime(g.starts_at)}</span>
              <span className="text-muted-foreground">{t("admin.responses.eventCount", { count: g.events.length })}</span>
              {(g.sharedClients.size > 0 || g.sharedIps.size > 0) && (
                <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium">
                  {t("admin.responses.sharedOriginFlag")}
                </span>
              )}
            </div>
            {(g.sharedClients.size > 0 || g.sharedIps.size > 0) && (
              <div className="px-3 py-2 text-[12px] text-amber-800 bg-amber-50/60 space-y-0.5">
                {[...g.sharedClients].map(([c, set]) => (
                  <p key={"c" + c}>
                    {t("admin.responses.sameBrowser", { id: c.slice(0, 8) })}{" "}
                    {[...set].map((pid) => g.events.find((e) => e.player_id === pid)?.player_name || pid.slice(0, 8)).join(", ")}
                  </p>
                ))}
                {[...g.sharedIps].map(([ip, set]) => (
                  <p key={"i" + ip}>
                    {t("admin.responses.sameIp", { ip })}{" "}
                    {[...set].map((pid) => g.events.find((e) => e.player_id === pid)?.player_name || pid.slice(0, 8)).join(", ")}
                  </p>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-mono uppercase text-xs tracking-wider px-3 py-1.5">{t("admin.responses.col.when")}</th>
                    <th className="text-left font-mono uppercase text-xs tracking-wider px-2 py-1.5">{t("admin.responses.col.player")}</th>
                    <th className="text-left font-mono uppercase text-xs tracking-wider px-2 py-1.5">{t("admin.responses.col.change")}</th>
                    <th className="text-left font-mono uppercase text-xs tracking-wider px-2 py-1.5">{t("admin.responses.col.via")}</th>
                    <th className="text-left font-mono uppercase text-xs tracking-wider px-2 py-1.5">{t("admin.responses.col.who")}</th>
                    <th className="text-left font-mono uppercase text-xs tracking-wider px-2 py-1.5">{t("admin.responses.col.origin")}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.events.map((e) => {
                    const shared =
                      (e.client_id && g.sharedClients.has(e.client_id)) || (e.ip && g.sharedIps.has(e.ip));
                    const changes = g.changesByPlayer.get(e.player_id) || 0;
                    return (
                      <tr key={e.id} className={cn("border-t", shared && "bg-amber-50/40")}>
                        <td className="px-3 py-1.5 whitespace-nowrap font-tabular">{fmtTime(e.changed_at)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap font-medium">
                          {e.player_name}
                          {changes > 1 && (
                            <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                              ×{changes}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {e.op === "delete" ? (
                            <span className="text-rose-600">{t("admin.responses.deleted")} ({statusLabel(e.old_status)})</span>
                          ) : (
                            <>
                              <span className="text-muted-foreground">{statusLabel(e.old_status)}</span>
                              <span className="mx-1">→</span>
                              <span className="font-medium">{statusLabel(e.new_status)}</span>
                            </>
                          )}
                          {(e.new_note || e.old_note) && (
                            <span className="block text-xs text-muted-foreground truncate max-w-[260px]">
                              {e.new_note || e.old_note}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{sourceLabel(e)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {e.actor_email || <span className="text-muted-foreground">{t("admin.responses.noLogin")}</span>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap font-mono text-xs text-muted-foreground" title={e.user_agent || ""}>
                          {e.client_id ? e.client_id.slice(0, 8) : "—"} · {e.ip || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
